import React, { useState } from 'react';
import styled from 'styled-components';
import ContactModal from './ContactModal';
import { API_BASE } from '../apiConfig';

const PageContainer = styled.div`
  width: 100%;
  background: #ffffff;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  padding: 80px 100px 60px;
  background: linear-gradient(135deg, #453793 0%, #5a4bb5 100%);
  color: white;
  text-align: center;

  h1 {
    font-size: 42px;
    font-weight: 700;
    margin-bottom: 20px;
    color: white;
  }

  p {
    font-size: 20px;
    max-width: 700px;
    margin: 0 auto;
    color: rgba(255, 255, 255, 0.9);
  }

  @media (max-width: 768px) {
    padding: 60px 20px 40px;
    h1 { font-size: 32px; }
    p { font-size: 18px; }
  }
`;

const ContentSection = styled.section`
  padding: 60px 100px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const ContactOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 60px;
`;

const ContactCard = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  border: 2px solid #e0e0e0;
  transition: all 0.3s ease;

  &:hover {
    border-color: #8ecf41;
    transform: translateY(-4px);
    box-shadow: 0 6px 20px rgba(142, 207, 65, 0.15);
  }

  h3 {
    font-size: 20px;
    color: #333;
    margin-bottom: 15px;
    font-weight: 600;
  }

  .icon {
    font-size: 36px;
    margin-bottom: 15px;
    display: block;
  }

  a {
    color: #453793;
    font-weight: 600;
    text-decoration: none;
    font-size: 18px;
    word-break: break-word;

    &:hover {
      color: #8ecf41;
    }
  }

  p {
    color: #666;
    font-size: 14px;
    margin-top: 10px;
    line-height: 1.5;
  }
`;

const DisclaimerBox = styled.div`
  background: #fff3cd;
  border-left: 5px solid #ffc107;
  padding: 20px 30px;
  border-radius: 8px;
  margin-bottom: 40px;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;

  p {
    color: #856404;
    font-size: 15px;
    margin: 0;
    line-height: 1.6;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    padding: 15px 20px;
    margin-left: 10px;
    margin-right: 10px;
  }
`;

const FormContainer = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const FormTitle = styled.h2`
  font-size: 28px;
  color: #333;
  margin-bottom: 30px;
  text-align: center;
  font-weight: 600;
`;

const Form = styled.form`
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 2px solid #e0e0e0;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 15px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    border-color: #8ecf41;
    outline: none;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
  font-family: inherit;
  resize: vertical;
  min-height: 150px;

  &:focus {
    border-color: #8ecf41;
    outline: none;
  }
`;

const Button = styled.button`
  padding: 15px 40px;
  background-color: #8ecf41;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    background-color: #72b237;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(142, 207, 65, 0.3);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const ResponseMessage = styled.p`
  margin-top: 20px;
  color: ${props => props.success ? '#8ecf41' : '#d32f2f'};
  font-weight: 600;
  text-align: center;
  font-size: 16px;
`;

function Bookings() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
    });

    const [responseMessage, setResponseMessage] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleCloseModal = () => {
        setResponseMessage(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (!formData.name || !formData.email || !formData.message) {
            setResponseMessage({
                type: 'error',
                text: 'Please fill out all required fields.'
            });
            return;
        }

        // Email validation
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(formData.email)) {
            setResponseMessage({
                type: 'error',
                text: 'Please enter a valid email address.'
            });
            return;
        }

        setIsSubmitting(true);


        try {
            const response = await fetch(`${API_BASE}/api/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setResponseMessage({
                    type: 'success',
                    text: 'Thank you for reaching out! We\'ll be in touch within 24 hours.'
                });
                // Clear form
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    message: '',
                });
            } else {
                const result = await response.json();
                setResponseMessage({
                    type: 'error',
                    text: result.error || 'Error submitting form. Please try again.'
                });
            }
        } catch (error) {
            setResponseMessage({
                type: 'error',
                text: 'Error submitting form. Please try again or contact us directly.'
            });
            console.error("Error submitting form:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <PageContainer>
            {/* HERO */}
            <HeroSection>
                <h1>Get In Touch</h1>
                <p>
                    Ready to discuss your technical challenges? Reach out via phone, email, or the form below.
                </p>
            </HeroSection>

            {/* CONTENT */}
            <ContentSection>
                {/* CONTACT OPTIONS */}
                <ContactOptions>
                    <ContactCard>
                        <span className="icon">📞</span>
                        <h3>Call Us</h3>
                        <a href="tel:+12483274996">1-248-327-4996</a>
                        <p>Monday - Friday, 9am - 5pm EST</p>
                    </ContactCard>

                    <ContactCard>
                        <span className="icon">✉️</span>
                        <h3>Email Us</h3>
                        <a href="mailto:sanspaugh@zumosoft.com">sanspaugh@zumosoft.com</a>
                        <p>We typically respond within 24 hours</p>
                    </ContactCard>

                    <ContactCard>
                        <span className="icon">💬</span>
                        <h3>Send a Message</h3>
                        <p>Fill out the form below and we'll get back to you as soon as possible</p>
                    </ContactCard>
                </ContactOptions>

                {/* DISCLAIMER */}
                <DisclaimerBox>
                    <p>
                        <strong>Note:</strong> This contact form is for potential clients only. Sales representatives,
                        resellers, and vendors—please respect our time and do not submit inquiries through this form.
                    </p>
                </DisclaimerBox>

                {/* CONTACT FORM */}
                <FormContainer>
                    <FormTitle>Send Us a Message</FormTitle>
                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <Label htmlFor="name">Name *</Label>
                            <Input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Your full name"
                                required
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="email">Email *</Label>
                            <Input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="you@company.com"
                                required
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="phone">Phone</Label>
                            <Input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="(123) 456-7890"
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="message">Message *</Label>
                            <TextArea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder="Tell us about your project or question..."
                                required
                            />
                        </FormGroup>

                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Sending...' : 'Send Message'}
                        </Button>
                    </Form>

                    {responseMessage && (
                        <ContactModal onClose={handleCloseModal}>
                            <ResponseMessage success={responseMessage.type === 'success'}>
                                {responseMessage.text}
                            </ResponseMessage>
                        </ContactModal>
                    )}
                </FormContainer>
            </ContentSection>
        </PageContainer>
    );
}

export default Bookings;
